import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Package } from 'lucide-react';
import { api } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';

type OC = {
  id: string; folio: string; fecha: string; estado: string;
  proveedor_nombre?: string; proveedor_rfc?: string;
  subtotal: number; iva: number; total: number;
  fecha_entrega_pactada?: string; porcentaje_recibido?: number;
};

export function OrdenesCompraPage() {
  const [estado, setEstado] = useState<string>('');
  const { data } = useQuery({
    queryKey: ['ordenes-compra', estado],
    queryFn: () => {
      const p = new URLSearchParams();
      if (estado) p.set('estado', estado);
      return api<OC[]>(`/ordenes-compra?${p}`);
    },
  });
  const items = data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Adquisiciones · Órdenes de compra</p>
          <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Órdenes de compra</h1>
          <p className="mt-1 text-[14px] text-ink-mute">
            Instrumentos formales de compra al proveedor. Al recibir se genera entrada de almacén, CxP y póliza.
          </p>
        </div>
        <Link to="/app/requisiciones"><Button variant="secondary">Ver requisiciones</Button></Link>
      </div>

      <div className="flex items-center gap-1 rounded-md border border-border bg-surface p-0.5 text-[12px] font-medium w-fit">
        {['', 'borrador', 'emitida', 'parcialmente_recibida', 'recibida', 'cancelada'].map((e) => (
          <button key={e || 'todas'} onClick={() => setEstado(e)}
            className={cn('rounded px-3 py-1.5 capitalize transition-colors',
              estado === e ? 'bg-brand-500 text-white' : 'text-ink-mute hover:bg-muted')}>
            {e ? e.replace(/_/g, ' ') : 'todas'}
          </button>
        ))}
      </div>

      <Card>
        <CardBody className="p-0">
          {items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <Package className="h-7 w-7 text-ink-soft" />
              <p className="text-[13px] text-ink-mute">Sin órdenes de compra. Autoriza una requisición para emitir OC.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-[13px]">
                <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                  <tr>
                    <th className="px-5 py-3">Folio</th>
                    <th className="px-5 py-3">Fecha</th>
                    <th className="px-5 py-3">Proveedor</th>
                    <th className="px-5 py-3">Entrega</th>
                    <th className="px-5 py-3 text-right">Total</th>
                    <th className="px-5 py-3 text-right">% Recibido</th>
                    <th className="px-5 py-3">Estado</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((o) => (
                    <tr key={o.id} className="transition-colors hover:bg-muted/50">
                      <td className="num px-5 py-3 font-semibold">{o.folio}</td>
                      <td className="num px-5 py-3 text-ink-mute">{fmt.date(o.fecha)}</td>
                      <td className="px-5 py-3">
                        <div className="text-ink">{o.proveedor_nombre ?? '—'}</div>
                        <div className="num text-[11px] text-ink-mute">{o.proveedor_rfc ?? ''}</div>
                      </td>
                      <td className="num px-5 py-3 text-ink-mute">{o.fecha_entrega_pactada ? fmt.date(o.fecha_entrega_pactada) : '—'}</td>
                      <td className="num px-5 py-3 text-right font-semibold">{fmt.money(o.total)}</td>
                      <td className="num px-5 py-3 text-right">{o.porcentaje_recibido != null ? `${Number(o.porcentaje_recibido).toFixed(0)}%` : '—'}</td>
                      <td className="px-5 py-3"><EstadoPill e={o.estado} /></td>
                      <td className="px-5 py-3 text-right">
                        <Link to={`/app/ordenes-compra/${o.id}`}><Button variant="ghost" size="sm">Ver</Button></Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function EstadoPill({ e }: { e: string }) {
  const cfg: Record<string, string> = {
    borrador: 'bg-stone-100 text-ink-mute',
    emitida: 'bg-sky-50 text-sky-700',
    parcialmente_recibida: 'bg-amber-50 text-amber-700',
    recibida: 'bg-emerald-50 text-emerald-700',
    cancelada: 'bg-rose-50 text-rose-700',
  };
  return <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', cfg[e] ?? 'bg-muted text-ink-mute')}>{e.replace(/_/g, ' ')}</span>;
}
